import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { EXPORT_ROLES, HOUSE_TILES, PAGE_SIZES, REGISTER_EDIT_ROLES, type HouseRow, type HouseTile } from '@mahallu/shared';
import { ApiError, loadErrorKey, registerSheet, useHouseSummary, useHouses, useMe, useWards } from '../api';
import { Icon } from '../components/Icon';
import { Badge, KpiTile, PageHeader } from '../components/Parts';
import { count, initials, inr, pageWindow } from '../format';
import { HouseDrawer } from './HouseDrawer';
import { HouseCreateForm } from './RegisterForms';
import { RegisterImport } from './RegisterImport';

function useFilters() {
  const [params, setParams] = useSearchParams();
  const tileParam = params.get('tile');
  const tile = (HOUSE_TILES as readonly string[]).includes(tileParam ?? '') ? (tileParam as HouseTile) : undefined;
  const sizeParam = Number(params.get('size'));
  const filters = {
    ward: params.get('ward') ?? '',
    status: params.get('status') ?? '',
    residence: params.get('residence') ?? '',
    q: params.get('q') ?? '',
    tile,
    page: Math.max(1, Number(params.get('page')) || 1),
    size: (PAGE_SIZES as readonly number[]).includes(sizeParam) ? sizeParam : 10,
    house: params.get('house'),
  };
  /** Changing any filter returns to page 1, like CampusTrack's directory. */
  const update = (next: Partial<Record<'ward' | 'status' | 'residence' | 'q' | 'tile' | 'page' | 'size' | 'house', string | number | null>>) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v === null || v === '' || v === undefined) p.delete(k);
          else p.set(k, String(v));
        }
        if (!('page' in next) && !('house' in next)) p.delete('page');
        return p;
      },
      { replace: 'house' in next },
    );
  };
  return { filters, update };
}

export function MemberManagement() {
  const { t, i18n } = useTranslation();
  const ml = i18n.language === 'ml';
  const { filters, update } = useFilters();
  const [search, setSearch] = useState(filters.q);
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const me = useMe();
  const canEdit = Boolean(me.data?.role && REGISTER_EDIT_ROLES.includes(me.data.role));
  const canExport = Boolean(me.data?.role && EXPORT_ROLES.includes(me.data.role));
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (search === filters.q) return;
    const id = setTimeout(() => update({ q: search.trim() }), 300);
    return () => clearTimeout(id);
  }, [search]); // only the typed text drives the debounce

  const base = { ward: filters.ward, status: filters.status, residence: filters.residence, q: filters.q };
  const wards = useWards();
  const summary = useHouseSummary(base);
  const list = useHouses({ ...base, tile: filters.tile, page: filters.page, size: filters.size });
  const s = summary.data;
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / filters.size));
  const from = total ? (filters.page - 1) * filters.size + 1 : 0;
  const to = Math.min(filters.page * filters.size, total);

  const notYet = () => setNotice(t('mm.notYet'));
  const download = (filters: Record<string, string | undefined>) => {
    setNotice(t('mm.preparing'));
    registerSheet.export(filters).then(
      () => setNotice(''),
      (e: unknown) => setNotice(e instanceof ApiError ? e.message : t('table.error')),
    );
  };
  const tile = (key: HouseTile) => ({
    pressed: (filters.tile ?? '') === key,
    onPress: () => update({ tile: filters.tile === key ? null : key }),
  });

  return (
    <>
      <PageHeader
        icon="users"
        title={t('mm.title')}
        desc={t('mm.desc')}
        actions={[
          ...(canExport
            ? [
                { label: t('mm.exportAll'), icon: 'down' as const, onClick: () => download({}) },
                { label: t('mm.currentView'), icon: 'down' as const, onClick: () => download({ ...base, tile: filters.tile }) },
              ]
            : []),
          ...(canEdit ? [{ label: t('mm.import'), icon: 'up' as const, onClick: () => setImporting(true) }] : []),
          { label: t('mm.wards'), icon: 'map', onClick: () => navigate('/register/wards') },
          { label: t('mm.ratePlans'), icon: 'wallet', onClick: () => navigate('/varisankhya/plans') },
          { label: t('mm.dataHealth'), icon: 'shield', onClick: () => navigate('/register/data-health') },
          ...(canEdit ? [{ label: t('mm.addHouse'), icon: 'plus' as const, primary: true, onClick: () => setAdding(true) }] : []),
        ]}
      />
      {notice ? (
        <div className="callout" role="status">
          {notice}
        </div>
      ) : null}

      <div className="filters">
        <select aria-label={t('f.ward')} value={filters.ward} onChange={(e) => update({ ward: e.target.value })}>
          <option value="">{t('f.allWards')}</option>
          {wards.data?.map((w) => (
            <option key={w.id} value={w.code}>
              {ml && w.nameMl ? w.nameMl : w.nameEn}
            </option>
          ))}
        </select>
        <select aria-label={t('f.status')} value={filters.status} onChange={(e) => update({ status: e.target.value })}>
          <option value="">{t('f.allStatus')}</option>
          <option value="ACTIVE">{t('status.ACTIVE')}</option>
          <option value="MOVED_OUT">{t('status.MOVED_OUT')}</option>
        </select>
        <select aria-label={t('f.residence')} value={filters.residence} onChange={(e) => update({ residence: e.target.value })}>
          <option value="">{t('f.anyResidence')}</option>
          <option value="abroad">{t('f.hasPravasi')}</option>
          <option value="other">{t('f.hasOtherState')}</option>
          <option value="resident">{t('f.allResident')}</option>
        </select>
        <span className="count num">{t('f.count', { count: total })}</span>
      </div>

      <div className="kpis">
        <KpiTile label={t('tile.all')} value={s?.houses ?? 0} icon="home" tone="brand" {...tile('all')} />
        <KpiTile label={t('tile.members')} value={s?.members ?? 0} icon="users" tone="teal" pressed={false} onPress={() => update({ tile: null })} />
        <KpiTile label={t('tile.pravasi')} value={s?.pravasi ?? 0} total={s?.houses} icon="plane" tone="violet" bar="violet" {...tile('pravasi')} />
        <KpiTile label={t('tile.other')} value={s?.other ?? 0} total={s?.houses} icon="map" tone="ok" bar="ok" {...tile('other')} />
        <KpiTile label={t('tile.phone')} value={s?.phone ?? 0} total={s?.houses} icon="phone" tone="bad" warning fixLabel={t('tile.fix')} {...tile('phone')} />
        <KpiTile label={t('tile.consent')} value={s?.consent ?? 0} total={s?.houses} icon="shield" tone="warn" warning fixLabel={t('tile.fix')} {...tile('consent')} />
        <KpiTile label={t('tile.arrears')} value={s?.arrears ?? 0} total={s?.houses} icon="wallet" tone="org" warning fixLabel={t('tile.fix')} {...tile('arrears')} />
      </div>

      <div className="tbl-wrap">
        <div className="tbl-bar">
          <div className="tbl-search">
            <Icon name="search" />
            <input type="search" aria-label={t('table.search')} placeholder={t('table.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="actions">
            <button type="button" className="btn sm" onClick={notYet} title={t('table.columns')}>
              <Icon name="cols" />
              {t('table.columns')}
            </button>
            <select aria-label={t('table.perPage', { n: filters.size })} value={filters.size} onChange={(e) => update({ size: Number(e.target.value) })}>
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {t('table.perPage', { n })}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('col.house')}</th>
                <th>{t('col.head')}</th>
                <th>{t('col.ward')}</th>
                <th>{t('col.members')}</th>
                <th>{t('col.plan')}</th>
                <th>{t('col.arrears')}</th>
                <th>{t('col.phone')}</th>
                <th>{t('col.status')}</th>
                <th>
                  <span className="sr-only">{t('col.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.isError ? (
                <tr>
                  <td colSpan={9} className="tbl-msg">
                    {t(loadErrorKey(list.error))}{' '}
                    <button type="button" className="btn sm" onClick={() => void list.refetch()}>
                      {t('table.retry')}
                    </button>
                  </td>
                </tr>
              ) : list.isPending ? (
                <tr>
                  <td colSpan={9} className="tbl-msg">
                    {t('table.loading')}
                  </td>
                </tr>
              ) : !list.data?.rows.length ? (
                <tr>
                  <td colSpan={9} className="tbl-msg">
                    {t('table.empty')}
                  </td>
                </tr>
              ) : (
                list.data.rows.map((h) => <Row key={h.id} h={h} ml={ml} onOpen={() => update({ house: h.id })} />)
              )}
            </tbody>
          </table>
        </div>
        <div className="pager">
          <span className="num">{t('table.showing', { from, to, total })}</span>
          <div className="pg">
            {pageWindow(filters.page, pages).map((p) => (
              <button key={p} type="button" aria-current={p === filters.page ? 'page' : undefined} onClick={() => update({ page: p })}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filters.house ? <HouseDrawer id={filters.house} onClose={() => update({ house: null })} /> : null}
      {importing ? <RegisterImport onClose={() => setImporting(false)} /> : null}
      {adding ? (
        <HouseCreateForm
          onClose={() => setAdding(false)}
          onCreated={(h) => {
            setAdding(false);
            update({ house: h.id });
          }}
        />
      ) : null}
    </>
  );
}

function Row({ h, ml, onOpen }: { h: HouseRow; ml: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <tr className="click" onClick={onOpen}>
      <td>
        <div className="who">
          <div className="av">{initials(h.nameEn)}</div>
          <div>
            <b>{h.nameEn}</b> {h.nameMl ? <span className="ml note">{h.nameMl}</span> : null}
            <small className="num">{h.houseNo}</small>
          </div>
        </div>
      </td>
      <td>{h.headName ?? '—'}</td>
      <td>{ml && h.ward.nameMl ? h.ward.nameMl : h.ward.nameEn}</td>
      <td>
        <span className="num">{count(h.memberCount)}</span>
        <span className="chip-row">
          {h.abroadCount ? <Badge tone="violet">{t('badge.abroad', { n: h.abroadCount })}</Badge> : null}
          {h.otherStateCount ? <Badge tone="teal">{t('badge.otherState', { n: h.otherStateCount })}</Badge> : null}
        </span>
      </td>
      <td>
        {t(`plan.${h.ratePlan}`)}
        {h.monthlyRateInr ? (
          <span className="note num">
            {' '}
            {inr(h.monthlyRateInr)}
            {t('unit.perMonth')}
          </span>
        ) : null}
      </td>
      <td>
        {h.arrearsMonths ? (
          <>
            <span className={`num${h.arrearsMonths >= 3 ? ' c-org' : ''}`}>{inr(h.arrearsInr)}</span>{' '}
            <span className="note">{t('unit.months', { n: h.arrearsMonths })}</span>
          </>
        ) : (
          <span className="c-muted">—</span>
        )}
      </td>
      <td>{h.phoneVerified ? <Badge tone="ok">{t('badge.verified')}</Badge> : <Badge tone="bad">{t('badge.notVerified')}</Badge>}</td>
      <td>
        <Badge tone={h.status === 'ACTIVE' ? 'ok' : 'neutral'}>{t(`status.${h.status}`)}</Badge>
      </td>
      <td>
        <button
          type="button"
          className="icon-btn"
          title={t('table.view')}
          aria-label={`${t('table.view')}: ${h.nameEn}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          <Icon name="eye" />
        </button>
      </td>
    </tr>
  );
}
